import { useQuery } from '@powersync/react';
import { useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  LayoutAnimationConfig,
  LinearTransition,
  useAnimatedRef,
} from 'react-native-reanimated';

import { NoteComposer } from '@/components/domain/NoteComposer';
import { type NoteEntryData } from '@/components/domain/NoteEntry';
import { NoteThread } from '@/components/domain/NoteThread';
import { NotFoundScreen } from '@/components/domain/NotFoundScreen';
import { ReferenceNote } from '@/components/domain/ReferenceNote';
import { ROW_LAYOUT_DURATION_MS, RunStepRow } from '@/components/domain/RunStepRow';
import { SealedLogHeader } from '@/components/domain/SealedLogHeader';
import { SealedRunSummary } from '@/components/domain/SealedRunSummary';
import { SealedStepRow } from '@/components/domain/SealedStepRow';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { BoundedKeyboardAwareScrollView } from '@/components/ui/BoundedKeyboardAwareScrollView';
import { Button } from '@/components/ui/Button';
import { NavBar } from '@/components/ui/NavBar';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Screen } from '@/components/ui/Screen';
import { StatusBanner } from '@/components/ui/StatusBanner';
import { StatusPill } from '@/components/ui/StatusPill';
import { Text } from '@/components/ui/Text';
import { useAuth } from '@/lib/auth';
import {
  addInstanceItemNote,
  completeInstance,
  setItemChecked,
  type Instance,
  type InstanceItem,
  type InstanceItemNote,
} from '@/lib/data';
import { clockTime, sealedDateTime } from '@/lib/format';
import { useGoBack } from '@/lib/navigation';
import { delayedSealUpload, escapedSeal } from '@/lib/seal';
import { usePendingInstanceIds, useSyncStatus } from '@/lib/sync-status';
import { useAsyncAction } from '@/lib/useAsyncAction';

export default function RunScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const goBack = useGoBack('/');
  const { session } = useAuth();

  const { data: instances } = useQuery<Instance>('SELECT * FROM instances WHERE id = ?', [
    id,
  ]);
  const { data: items } = useQuery<InstanceItem>(
    'SELECT * FROM instance_items WHERE instance_id = ? ORDER BY position, id',
    [id],
  );
  const { data: notes } = useQuery<InstanceItemNote>(
    'SELECT * FROM instance_item_notes WHERE instance_id = ? ORDER BY created_at',
    [id],
  );
  // Attribution names resolve live from the template's member roster (joined on
  // the actor id), so a rename propagates and nothing is snapshotted. The run's
  // template_id keys the lookup.
  const { data: memberProfiles } = useQuery<{
    user_id: string;
    display_name: string | null;
  }>('SELECT user_id, display_name FROM template_member_profiles WHERE template_id = ?', [
    instances[0]?.template_id ?? '',
  ]);
  // Fallback for the current user's own actions before their roster row has
  // synced (e.g. a run started offline on a freshly-created template).
  const { data: selfProfile } = useQuery<{ display_name: string | null }>(
    'SELECT display_name FROM profiles WHERE id = ?',
    [session?.user.id ?? ''],
  );
  const instance = instances[0] ?? null;

  const me = session?.user.id;
  const nameFor = useCallback(
    (userId: string | null): string | null => {
      if (!userId) return null;
      const found = memberProfiles.find((m) => m.user_id === userId)?.display_name;
      if (found) return found;
      if (userId === me) return selfProfile[0]?.display_name ?? 'Unknown user';
      return 'Unknown user';
    },
    [memberProfiles, selfProfile, me],
  );
  // Who started the run — no longer an authority (anyone with template access can
  // check off and seal), only used to tailor the shared-run hint.
  const startedByMe = !!instance && instance.owner_id === session?.user.id;

  // The run's seal: when it was made, and when the server learned of it. Both drive
  // what the sealed record can and can't vouch for.
  const sealStatus = instance?.status ?? null;
  const sealedAt = instance?.completed_at ?? null;
  const sealRecordedAt = instance?.completed_recorded_at ?? null;
  const frozen = sealStatus === 'completed';
  const seal = useMemo(
    () => ({
      status: sealStatus,
      completed_at: sealedAt,
      completed_recorded_at: sealRecordedAt,
    }),
    [sealStatus, sealedAt, sealRecordedAt],
  );

  // Did this check or note escape the sealed record? Two different ways it can, both
  // invisible on screen otherwise — see escapedSeal.
  const lateOnRun = useCallback(
    (recordedAt: string | null | undefined, authoredAt: string | null | undefined) =>
      escapedSeal(seal, recordedAt, authoredAt),
    [seal],
  );

  // Set only when the seal reached the server meaningfully later than it was made —
  // it didn't go up with the run, which the seal time alone can't tell you.
  const uploadedAt = useMemo(() => {
    const iso = delayedSealUpload(seal);
    return iso ? sealedDateTime(iso) : undefined;
  }, [seal]);

  const { state: syncState } = useSyncStatus();
  const pendingInstanceIds = usePendingInstanceIds();
  const unsynced = frozen && pendingInstanceIds.has(id);
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();

  const { busy: completing, error, setError, run: runComplete } = useAsyncAction();
  const [composingItemId, setComposingItemId] = useState<string | null>(null);
  const [submittingNote, setSubmittingNote] = useState(false);

  // While the reorder animation plays, rows are sliding under the finger and a
  // second tap would re-toggle the item. Lock all toggles for the animation's
  // duration. The ref is the source of truth (synchronous, blocks a fast
  // double-tap in the same frame); `animating` just drives the disabled UI.
  const [animating, setAnimating] = useState(false);
  const lockRef = useRef(false);
  const unlockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (unlockTimer.current) clearTimeout(unlockTimer.current);
    },
    [],
  );

  // Group notes by step, mapped to the NoteThread shape.
  const notesByItem = useMemo(() => {
    const map = new Map<string, NoteEntryData[]>();
    for (const n of notes) {
      const list = map.get(n.instance_item_id) ?? [];
      list.push({
        id: n.id,
        author: nameFor(n.author_id) ?? 'Unknown user',
        time: clockTime(n.created_at),
        body: n.body,
        late: lateOnRun(n.recorded_at, n.created_at),
      });
      map.set(n.instance_item_id, list);
    }
    return map;
  }, [notes, nameFor, lateOnRun]);

  // No in-flight guard here: NoteComposer already wraps this handler in its own
  // runExclusive (paired with clear-draft-only-on-success). A guard at this level
  // would make a dropped duplicate resolve cleanly, which the composer reads as
  // success — clearing the draft without a note ever being posted.
  async function submitNote(itemId: string, body: string) {
    setSubmittingNote(true);
    setError(null);
    try {
      await addInstanceItemNote({ instance_id: id, instance_item_id: itemId, body });
      setComposingItemId(null);
    } catch (e) {
      setError((e as Error).message);
      // Re-throw so the composer keeps the user's draft (the note is immutable
      // once posted, so we can't silently lose it).
      throw e;
    } finally {
      setSubmittingNote(false);
    }
  }

  const done = useMemo(() => items.filter((i) => i.checked).length, [items]);
  const { checked, remaining } = useMemo(
    () => ({
      checked: items
        .filter((i) => i.checked)
        .sort((a, b) => (a.checked_at ?? '').localeCompare(b.checked_at ?? '')),
      remaining: items.filter((i) => !i.checked),
    }),
    [items],
  );
  // Who sealed the run — the person who hit Complete, recorded server-side on the
  // row (completed_by, re-stamped from the caller).
  const sealedBy = useMemo(
    () => nameFor(instance?.completed_by ?? null),
    [instance?.completed_by, nameFor],
  );

  async function toggle(item: InstanceItem) {
    if (frozen || lockRef.current) return;
    lockRef.current = true;
    setAnimating(true);
    try {
      await setItemChecked(item.id, !item.checked);
    } catch (e) {
      setError((e as Error).message);
    }
    // Release once the reorder animation (which starts on the post-write
    // re-render) has had time to settle.
    if (unlockTimer.current) clearTimeout(unlockTimer.current);
    unlockTimer.current = setTimeout(() => {
      lockRef.current = false;
      setAnimating(false);
    }, ROW_LAYOUT_DURATION_MS);
  }

  async function complete() {
    await runComplete(async () => {
      await completeInstance(id);
    });
  }

  if (!instance) {
    return (
      <NotFoundScreen
        onBack={goBack}
        navTitle="Run"
        title="Run not found"
        body="This run may have been deleted, or you don't have access to it."
      />
    );
  }

  const renderStep = (item: InstanceItem) => {
    const itemNotes = notesByItem.get(item.id) ?? [];
    return (
      <RunStepRow
        key={item.id}
        label={item.label}
        checked={!!item.checked}
        timeLabel={item.checked_at ? clockTime(item.checked_at) : null}
        by={item.checked ? nameFor(item.checked_by) : null}
        disabled={animating}
        onToggle={() => toggle(item)}
        onAddNote={() => setComposingItemId(item.id)}
      >
        {item.notes ? <ReferenceNote text={item.notes} /> : null}
        {itemNotes.length > 0 ? <NoteThread notes={itemNotes} /> : null}
        {composingItemId === item.id ? (
          <NoteComposer
            onSubmit={(body) => submitNote(item.id, body)}
            busy={submittingNote}
          />
        ) : null}
      </RunStepRow>
    );
  };

  return (
    <Screen edges={['top']}>
      <NavBar
        onBack={goBack}
        center={
          <StatusPill
            tone={frozen ? 'complete' : 'active'}
            label={frozen ? 'Sealed run' : 'Active run'}
          />
        }
      />

      <SyncStatusArea scrollRef={syncScrollRef}>
        <BoundedKeyboardAwareScrollView
          scrollRef={syncScrollRef}
          alwaysBounceVertical
          contentClassName="gap-4 px-5 pb-10 pt-1"
        >
          {unsynced ? (
            <StatusBanner
              variant="caution"
              icon="dot"
              title="Not synced yet"
              subtitle="This sealed run is saved on this device. It will upload when you reconnect"
              showSpinner={syncState === 'syncing'}
            />
          ) : null}
          {frozen ? (
            <View className="gap-4">
              <SealedLogHeader
                title={instance.title}
                description={instance.description}
              />
              <SealedRunSummary
                done={done}
                total={items.length}
                sealedBy={sealedBy || undefined}
                sealedAt={sealedAt ? sealedDateTime(sealedAt) : ''}
                uploadedAt={uploadedAt}
              />
            </View>
          ) : (
            <View className="gap-3">
              <Text variant="title">{instance.title}</Text>
              {instance.description ? (
                <Text variant="body" className="text-foreground-secondary">
                  {instance.description}
                </Text>
              ) : null}
              <View className="flex-row items-center gap-3">
                <ProgressBar value={done} total={items.length} className="flex-1" />
                <Text variant="accent">
                  {done}/{items.length}
                </Text>
              </View>
              {!startedByMe ? (
                <Text variant="caption">Shared run — check items off together.</Text>
              ) : null}
            </View>
          )}

          {frozen ? (
            <View>
              {items.map((item, idx) => {
                const itemNotes = notesByItem.get(item.id) ?? [];
                return (
                  <SealedStepRow
                    key={item.id}
                    label={item.label}
                    checked={!!item.checked}
                    timeLabel={item.checked_at ? clockTime(item.checked_at) : null}
                    by={item.checked ? nameFor(item.checked_by) : null}
                    late={lateOnRun(item.checked_recorded_at, item.checked_at)}
                    isFirst={idx === 0}
                    isLast={idx === items.length - 1}
                  >
                    {item.notes ? <ReferenceNote text={item.notes} flush /> : null}
                    {itemNotes.length > 0 ? (
                      <NoteThread notes={itemNotes} bubble />
                    ) : null}
                  </SealedStepRow>
                );
              })}
            </View>
          ) : (
            // Rows + the "Completed" header live in ONE array so every child keeps
            // a stable element identity across the boundary — React namespaces keys
            // per array, so separate maps would unmount/remount moving children and
            // defeat the Reanimated layout (move) animation. `skipEntering` keeps the
            // header from fading in on the first paint (e.g. reopening a run that
            // already has checked items) while still fading it in/out mid-session.
            <LayoutAnimationConfig skipEntering>
              {/* `relative` gives the reorder (layout) animation a positioning
                context, so a row animates from its own position rather than the
                viewport top when it moves to the Completed group at the bottom. */}
              <View className="relative">
                {[
                  ...remaining.map((i) => renderStep(i)),
                  checked.length > 0 && remaining.length > 0 ? (
                    <Animated.View
                      key="completed-header"
                      layout={LinearTransition.duration(ROW_LAYOUT_DURATION_MS)}
                      entering={FadeIn.duration(ROW_LAYOUT_DURATION_MS)}
                      exiting={FadeOut.duration(ROW_LAYOUT_DURATION_MS)}
                      className="mb-1 mt-3"
                    >
                      <Text variant="accent">Completed</Text>
                    </Animated.View>
                  ) : null,
                  ...checked.map((i) => renderStep(i)),
                ]}
              </View>
            </LayoutAnimationConfig>
          )}

          {error ? (
            <Text variant="caption" className="text-alert">
              {error}
            </Text>
          ) : null}

          {frozen ? (
            <Text variant="caption" className="py-2 text-center text-faint">
              🔒 This run is a permanent, read-only record.
            </Text>
          ) : (
            <View className="gap-2 pt-2">
              <Button label="Complete run" busy={completing} onPress={complete} />
            </View>
          )}
        </BoundedKeyboardAwareScrollView>
      </SyncStatusArea>
    </Screen>
  );
}
